/**
 * Lea (the lea_ilearn engine) client + the Lea console (admin) client.
 *
 * iLearn mints a short-lived Lea token for the signed-in user (POST /v1/lea/token); the panel sends it
 * to the engine at /lea/* (same origin: Vite proxy in development, ingress in production).
 * The console talks to iLearn's own backend (/v1/lea/console/*), which holds the settings and proxies
 * the engine's admin endpoints with the service key.
 */
import axiosInstance from '@/api/axiosInstance';
import { tr } from '@/i18n';
import { LEA_BASE } from '@/api/endpoints/api.constants';

export interface LeaAction {
  label: string;
  token?: string;
  say?: string;
  href?: string;
  style?: 'primary' | 'secondary' | 'danger';
}

export interface LeaCard {
  type: string;
  id?: number;
  user_id?: number;
  title?: string;
  subtitle?: string | null;
  summary?: string | null;
  progress?: number | null;
  due_date?: string | null;
  state?: string;
  url?: string;
  duration_minutes?: number | null;
  rating?: number | null;
  completed?: number;
  total?: number;
  overdue?: number;
  kind?: string;
  // sessions
  when?: string;
  starts_at?: string | null;
  location?: string | null;
  trainer?: string | null;
  seats_left?: number | null;
  // skills
  required_level?: string | null;
  current_level?: string | null;
  // communities / posts
  members?: number | null;
  created_at?: string | null;
  comments?: number | null;
  likes?: number | null;
  actions?: LeaAction[];
}

export interface LeaReply {
  text: string;
  language: string;
  source: string;
  cards: LeaCard[];
  actions: LeaAction[];
  panel: Record<string, unknown> | null;
  intent: string;
  agent: string;
  ai_used: boolean;
  conversation_id?: string;
}

export interface LeaConfig {
  mode: string;
  ai_available: boolean;
  roles: string[];
  languages: string[];
  lenses: string[];
  name: string;
  greeting: string;
  suggestions: LeaAction[];
}

export interface LeaConversation {
  id: string;
  title: string;
  updated_at: number;
}

export interface LeaTurn {
  role: 'user' | 'lea';
  text: string;
  intent?: string;
  agent?: string;
  payload?: LeaReply | null;
}

export interface LeaNudge {
  id: number;
  kind: string;
  text: string;
  url: string;
  created_at: string | number;
}

export type LeaMode = 'standard' | 'ai';

/** What the page the person is on tells Lea (a community brief, a quiz for the course being edited…). */
export function leaPageContext(pathname: string): Record<string, unknown> {
  const page: Record<string, unknown> = { path: pathname };
  let m = pathname.match(/^\/user\/community\/(\d+)/);
  if (m) page.community_id = Number(m[1]);
  m = pathname.match(/^\/admin\/learning\/courses\/build\/(\d+)/) || pathname.match(/^\/user\/course(?:-start)?\/(\d+)/);
  if (m) page.course_id = Number(m[1]);
  m = pathname.match(/^\/user\/learning-path\/(\d+)/);
  if (m) page.program_id = Number(m[1]);
  return page;
}

let cached: { token: string; until: number; lang: string } | null = null;

async function token(lang: string, force = false): Promise<string> {
  if (!force && cached && cached.lang === lang && cached.until > Date.now()) return cached.token;
  let data: { token: string; expires_in: number };
  try {
    ({ data } = await axiosInstance.post<{ token: string; expires_in: number }>('/v1/lea/token', { lang }));
  } catch (e: any) {
    // e.g. 503 "Lea is not configured on this server." — show that, not
    // axios' "Request failed with status code 503".
    const detail = e?.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : tr('lea.unavailable', 'Lea is unavailable right now.'));
  }
  cached = { token: data.token, until: Date.now() + Math.max(60, data.expires_in - 60) * 1000, lang };
  return data.token;
}

async function call<T>(path: string, lang: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers: Record<string, string> = { 'X-Lea-Token': await token(lang, !retry), ...((init.headers as Record<string, string>) || {}) };
  if (!(init.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${LEA_BASE}${path}`, { ...init, headers });
  if (res.status === 401 && retry) return call<T>(path, lang, init, false);
  // No engine behind /lea (e.g. not deployed): the SPA answers with index.html.
  if (res.ok && !(res.headers.get('content-type') || '').includes('json')) {
    throw new Error(tr('lea.unavailable', 'Lea is unavailable right now.'));
  }
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* not JSON */ }
    throw new Error(detail || tr('lea.unavailable_status', 'Lea is unavailable ({status}).', { status: res.status }));
  }
  return res.json() as Promise<T>;
}

export const LeaApi = {
  config: (lang: string) => call<LeaConfig>('/config', lang),
  ask: (lang: string, body: { message?: string; action?: { token: string }; page?: Record<string, unknown>; conversation_id?: string | null; mode?: LeaMode }) =>
    call<LeaReply>('/ask', lang, { method: 'POST', body: JSON.stringify({ ...body, language: lang }) }),
  conversations: (lang: string) => call<{ items: LeaConversation[] }>('/conversations', lang),
  conversation: (lang: string, id: string) => call<{ id: string; turns: LeaTurn[] }>(`/conversations/${id}`, lang),
  deleteConversation: (lang: string, id: string) => call<{ ok: boolean }>(`/conversations/${id}`, lang, { method: 'DELETE' }),
  /** Attach a document: Lea reads it and answers with what it can do (summarise / course / quiz, by role). */
  upload: (lang: string, file: File, conversationId?: string | null) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('language', lang);
    if (conversationId) fd.append('conversation_id', conversationId);
    return call<LeaReply & { name: string; chars: number }>('/upload', lang, { method: 'POST', body: fd });
  },
  nudges: (lang: string) => call<{ items: LeaNudge[] }>('/nudges', lang),
  nudgeSeen: (lang: string, id: number) => call<{ ok: boolean }>(`/nudges/${id}/seen`, lang, { method: 'POST' }),
};

// ── Console (admin) ───────────────────────────────────────────────────────
export interface LeaModel {
  id: string;
  name: string;
  provider: string;
  model: string;
  base_url?: string | null;
  api_version?: string | null;
  temperature?: number | null;
  max_tokens?: number | null;
  is_default: boolean;
  has_key: boolean;
}

export interface LeaAgentSetting { enabled: boolean; readonly: boolean; tone: string; instructions: string }
export interface LeaAutomationSetting { enabled: boolean; hour: number; weekday: number | null; title?: string }

/** Narration (AI audio in Studio lessons). The ElevenLabs key is never sent back: only whether one is saved. */
export type LeaNarrationProvider = 'elevenlabs' | 'openai';
export interface LeaNarration {
  provider: LeaNarrationProvider;
  has_key: boolean;
  elevenlabs_voice_id: string;
  elevenlabs_model_id: string;
  openai_voice: string;
  /** The console model whose key OpenAI narration uses (none = OpenAI narration can't run). */
  openai_model: { id: string; name: string } | null;
  ready: boolean;
}
export interface LeaNarrationVoice {
  voice_id: string;
  name: string;
  category?: string | null;
  labels?: Record<string, string> | null;
  description?: string | null;
  preview_url?: string | null;
}
export interface LeaNarrationPatch {
  provider?: LeaNarrationProvider;
  api_key?: string;
  clear_key?: boolean;
  elevenlabs_voice_id?: string;
  elevenlabs_model_id?: string;
  openai_voice?: string;
}
export interface LeaNarrationTest { ok: boolean; provider: LeaNarrationProvider; voices?: LeaNarrationVoice[]; ms?: number; code?: string; error?: string }

export interface LeaConsoleSettings {
  operating_mode: 'intelligence' | 'ai' | 'disabled';
  agents: Record<string, LeaAgentSetting>;
  role_policy: Record<string, string[]>;
  models: LeaModel[];
  narration: LeaNarration;
  indexed_community_ids: number[];
  mcp: { enabled: boolean; disabled_tools: string[] };
  automations: Record<string, LeaAutomationSetting>;
}

export interface LeaCapabilityInfo { name: string; description: string; kind: 'read' | 'write'; audiences: string[]; internal: boolean }

export interface LeaConsole {
  settings: LeaConsoleSettings;
  engine: { ok: boolean; version?: string; mode?: string; uptime_s?: number; error?: string;
    ilearn?: { reachable: boolean; capabilities: number };
    agents?: { id: string; name: string; intents: string[]; audiences: string[] }[]; jobs?: string[] };
  agent_ids: string[];
  automation_catalog: Record<string, LeaAutomationSetting>;
  capabilities: LeaCapabilityInfo[];
}

export interface LeaOverview {
  version: string; uptime_s: number;
  agents: { id: string; name: string; intents: string[]; audiences: string[]; modes: string[] }[];
  jobs: string[];
  stats: Record<string, number | string>;
  automations: { id: string; title: string; description: string; weekly: boolean }[];
}

export const LeaConsoleApi = {
  get: async () => (await axiosInstance.get<LeaConsole>('/v1/lea/console')).data,
  update: async (patch: Partial<LeaConsoleSettings>) => (await axiosInstance.put<LeaConsoleSettings>('/v1/lea/console', patch)).data,
  upsertModel: async (model: Partial<LeaModel> & { api_key?: string }) => (await axiosInstance.post<LeaConsoleSettings>('/v1/lea/console/models', model)).data,
  deleteModel: async (id: string) => (await axiosInstance.delete<LeaConsoleSettings>(`/v1/lea/console/models/${id}`)).data,
  testModel: async (id: string) => (await axiosInstance.post<{ ok: boolean; reply?: string; ms?: number; error?: string }>(`/v1/lea/console/models/${id}/test`)).data,
  overview: async () => (await axiosInstance.get<LeaOverview>('/v1/lea/console/overview')).data,
  audit: async (kind: 'audit' | 'conversations', limit = 100) =>
    (await axiosInstance.get<{ items: Record<string, unknown>[] }>('/v1/lea/console/audit', { params: { kind, limit } })).data,
  runAutomation: async (flow: string) => (await axiosInstance.post<{ ok: boolean; flow?: string; nudges?: number; ms?: number; error?: string }>(`/v1/lea/console/automations/${flow}/run`)).data,
  mcpToken: async () => (await axiosInstance.get<{ token: string; expires_in: number; tools_url: string; call_url: string }>('/v1/lea/console/mcp-token')).data,
  updateNarration: async (patch: LeaNarrationPatch) => (await axiosInstance.put<LeaConsoleSettings>('/v1/lea/console/narration', patch)).data,
  /** Lists the provider's voices; `api_key` tests a key typed in the form before it is saved. */
  testNarration: async (body: { provider: LeaNarrationProvider; api_key?: string }) =>
    (await axiosInstance.post<LeaNarrationTest>('/v1/lea/console/narration/test', body)).data,
};
